import { useEffect, useRef, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import { LanguageOutlined } from '@mui/icons-material';
import styles from './LanguageSwitcher.module.scss';
import { languages, maxMobileSize } from '../../utils/constants';
import colors from '../../scss/colors';
import { useWindowSize } from '../../utils/hooks';
import { normalizeLocale } from '../../utils/i18nHelpers';

function LanguageSwitcher({ gap = '10px', isDark = false, collapsed }) {
  const { i18n } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState(i18n?.language || 'en'); // <-- default to auto-detected language
  const { width } = useWindowSize();
  const containerRef = useRef(null);

  const handleLanguageChange = (lang) => {
    const normalizedLang = normalizeLocale(lang);
    i18n.changeLanguage(normalizedLang);
    window.localStorage.setItem('lang', normalizedLang);
    setSelectedLanguage(normalizedLang);
  };

  const selectStyled = {
    display: 'flex',
    flexDirection: 'column',
    fontSize: '14px',
    padding: 0,
    gap: 'calc(var(--spacing) * 0.5)',
    '& :hover': {
      borderColor: 'transparent'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent'
    },

    '& .MuiSelect-select': {
      padding: '0px !important',
      paddingRight: '0px !important',
      paddingLeft: gap,
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '16px',
      color: isDark ? colors.WHITE : colors.NAVY_BLUE,
      fontFamily: 'Gotham'
    },
    '@media only screen and (max-width: 997px)': {
      paddingLeft: '0px',
      paddingTop: '0px',
      paddingBottom: '0px'
    }
  };

  useEffect(() => {
    const savedLang = window.localStorage.getItem('lang');
    const normalizedLang = normalizeLocale(savedLang || i18n.language);

    i18n.changeLanguage(normalizedLang);
    setSelectedLanguage(normalizedLang);
  }, [i18n]);

  useEffect(() => {
    const handleLangChanged = () => {
      requestAnimationFrame(() => {
        containerRef.current?.querySelector('[role="combobox"]')?.focus();
      });
    };
    i18n.on('languageChanged', handleLangChanged);
    return () => i18n.off('languageChanged', handleLangChanged);
  }, [i18n]);

  const normalizedSelectedLanguage = normalizeLocale(selectedLanguage);

  return (
    <div ref={containerRef} className={`${styles.language_switcher}`}>
      <FormControl className={isDark ? `${styles.dark}` : `${styles.formControl}`}>
        <Select
          id="language-select"
          value={normalizedSelectedLanguage}
          onChange={(event) => handleLanguageChange(event.target.value)}
          inputProps={{ 'aria-label': 'Without label' }}
          IconComponent={() => null}
          renderValue={(value) => (
            <div className="flex flex-row items-center gap-2">
              {collapsed ? (
                <Tooltip title="Language" placement="right" arrow>
                  <span className="opacity-60">
                    <LanguageOutlined sx={{ fontSize: 20 }} />
                  </span>
                </Tooltip>
              ) : (
                <>
                  <span className="opacity-60">
                    <LanguageOutlined sx={{ fontSize: 20 }} />
                  </span>
                  {width >= maxMobileSize && (
                    <span className="text-sm font-medium text-white font-[Gotham]">
                      {' '}
                      {languages.find((l) => l.key === value)?.value}
                    </span>
                  )}
                </>
              )}
            </div>
          )}
          sx={selectStyled}
        >
          {languages?.map((lang) => (
            <MenuItem key={lang.key} value={lang.key}>
              {lang.value}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
}

export default LanguageSwitcher;
